import { NumberInput, Tooltip } from "@mantine/core";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { useEffect, useState } from "react";
import type { FoerderRegime } from "../../../../types/api.types";
import { getNeedsGebotstermin } from "../../../../utils/getNeedsGebotstermin";
import { getNeedsJahrDesGebotstermins } from "../../../../utils/getNeedsJahrDesGebotstermins";
import type {
  ExpressOnboardingEegColumnProps,
  ExpressOnboardingGenerator
} from "../../ExpressOnboarding.types";
import { useExpressOnboardingEegSave } from "../hooks/useExpressOnboardingEegSave";

function getExpressOnboardingTableJahrDesGebotsterminsColumn({
  getError,
  setError,
  getDraft,
  updateDraft,
  clearDraft,
  clearError
}: ExpressOnboardingEegColumnProps): MRT_ColumnDef<ExpressOnboardingGenerator> {
  return {
    id: "jahr_des_gebotstermins",
    accessorKey: "jahr_des_gebotstermins",
    header: "Jahr des Gebotstermins",
    enableGlobalFilter: false,
    Cell: ({ cell, row }) => {
      const value =
        cell.getValue<ExpressOnboardingGenerator["jahr_des_gebotstermins"]>();
      const rowDraft = getDraft(row.original.id);
      const draftOrValue =
        rowDraft && "jahr_des_gebotstermins" in rowDraft
          ? (rowDraft.jahr_des_gebotstermins ?? null)
          : value;
      const foerderregime =
        rowDraft && "foerderregime" in rowDraft
          ? rowDraft.foerderregime
          : row.original.foerderregime;

      const usesJahrDesGebotstermins =
        !!foerderregime &&
        getNeedsJahrDesGebotstermins(foerderregime as FoerderRegime);

      /* local state while typing, so a keystroke does not re-render the whole table */
      const [currentValue, setCurrentValue] =
        useState<ExpressOnboardingGenerator["jahr_des_gebotstermins"]>(
          draftOrValue
        );

      useEffect(() => {
        setCurrentValue(draftOrValue);
      }, [draftOrValue]);

      const { save, rowIsSaving } = useExpressOnboardingEegSave(row.original, {
        getError,
        setError,
        getDraft,
        updateDraft,
        clearDraft,
        clearError
      });

      const cantEditReason =
        row.original.uneditable_fields?.jahr_des_gebotstermins;

      return (
        <Tooltip
          disabled={!cantEditReason && usesJahrDesGebotstermins}
          label="Ein Jahr des Gebotstermins kann nur für Förderregime mit Ausschreibung hinterlegt werden."
        >
          <NumberInput
            decimalScale={0}
            disabled={
              !!cantEditReason || !usesJahrDesGebotstermins || rowIsSaving
            }
            error={getError(row.original.id, "jahr_des_gebotstermins")}
            loading={rowIsSaving}
            thousandSeparator={false}
            value={currentValue ?? ""} // Mantine ignores null
            onBlur={() => {
              const hasChange = currentValue !== value;
              if (!hasChange) {
                clearDraft(row.original.id, "jahr_des_gebotstermins");
                return;
              }

              updateDraft(row.original.id, {
                jahr_des_gebotstermins: currentValue ?? null
              });
              save({ jahr_des_gebotstermins: currentValue ?? null });
            }}
            onChange={(newValue) => {
              const numericNewValue =
                typeof newValue === "number"
                  ? newValue
                  : newValue === ""
                    ? null
                    : parseInt(newValue, 10);
              setCurrentValue(numericNewValue);
              /* the Gebotstermin cell has to be enabled/disabled while typing,
                 so it can be reached (or skipped) with the tab key */
              if (
                getIsGebotsterminNeeded(numericNewValue) !==
                getIsGebotsterminNeeded(draftOrValue)
              ) {
                updateDraft(row.original.id, {
                  jahr_des_gebotstermins: numericNewValue
                });
              }
              if (numericNewValue) {
                clearError(row.original.id, "jahr_des_gebotstermins");
              }
            }}
          />
        </Tooltip>
      );
    }
  };
}

function getIsGebotsterminNeeded(
  jahrDesGebotstermins: ExpressOnboardingGenerator["jahr_des_gebotstermins"]
) {
  return !!jahrDesGebotstermins && getNeedsGebotstermin(jahrDesGebotstermins);
}

export { getExpressOnboardingTableJahrDesGebotsterminsColumn };
